import React, { useState, useEffect } from 'react';
import { Receipt, DollarSign, Clock } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const PurchaseHistory = ({ gameId }) => {
  const [history, setHistory] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadHistory(); }, [gameId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/store/purchase-history/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.transactions || []);
        setReceipts(data.inbox_receipts || []);
        setTotalSpent(data.total_spent || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const formatAmount = (cents) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Merge Stripe transactions and inbox-only receipts, dedupe by date proximity
  const allItems = [
    ...history.map(t => ({ ...t, source: 'stripe', sortDate: t.date })),
    ...receipts
      .filter(r => !history.some(t => Math.abs(new Date(t.date) - new Date(r.date)) < 5000))
      .map(r => ({ package_name: r.title, body: r.body, date: r.date, source: 'inbox', sortDate: r.date })),
  ].sort((a, b) => new Date(b.sortDate || 0) - new Date(a.sortDate || 0));

  if (loading) return <p className="text-[#E8E0D0]/20 text-xs animate-pulse p-8 text-center">Loading history...</p>;

  return (
    <div data-testid="purchase-history">
      <div className="flex items-center gap-2 mb-1">
        <Receipt size={16} className="text-[#C9A84C]" />
        <h3 className="font-['Cormorant_Garamond'] text-[#C9A84C] text-lg font-bold">PURCHASE HISTORY</h3>
      </div>
      <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest mb-4">
        ALL TRANSACTIONS — TOTAL SPENT: <span className="text-[#C9A84C]">{formatAmount(totalSpent)}</span>
      </p>

      {allItems.length === 0 ? (
        <div className="text-center py-12">
          <Receipt size={24} className="text-[#E8E0D0]/10 mx-auto mb-2" />
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs">No purchases yet.</p>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/15 italic text-sm mt-1">"The first move is always the hardest."</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {allItems.map((item, i) => (
            <div key={i} className="bg-[#111820] border border-[#1A2535] rounded-lg p-3 flex items-center justify-between gap-3 hover:border-[#C9A84C]/15 transition-all"
              data-testid={`purchase-item-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{item.package_name}</span>
                  {item.source === 'stripe' && item.amount > 0 && (
                    <span className="flex items-center gap-0.5 font-['Outfit'] text-[#4ADE80] text-[9px]">
                      <DollarSign size={9} />{(item.amount / 100).toFixed(2)}
                    </span>
                  )}
                </div>
                {item.body && <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-0.5">{item.body}</p>}
                {item.effect && !item.body && <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-0.5">Effect: {item.effect}</p>}
              </div>
              <div className="text-right shrink-0">
                <div className="flex items-center gap-1 text-[#E8E0D0]/20">
                  <Clock size={9} />
                  <span className="font-['Outfit'] text-[8px]">{formatDate(item.date)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PurchaseHistory;
